/**
 * Protected route wrapper that requires authentication and optional scope
 * Redirects to login if not authenticated, to unauthorized if missing scope
 */

import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredScope?: string;
}

export function ProtectedRoute({ children, requiredScope }: ProtectedRouteProps) {
  const { isAuthenticated, hasScope, isRestoringScopes, restoreError } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // The key is stored but could not be verified (auth unreachable). That is
  // not a revoked key: let the route through and say so, rather than
  // logging the user out of every tab because of an outage.
  if (restoreError) {
    return (
      <>
        <div role="status" className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          Could not verify your key; some actions may be unavailable.
        </div>
        {children}
      </>
    );
  }

  // A full page load restores the session key synchronously but the scopes
  // asynchronously. Judging requiredScope against not-yet-loaded scopes sent
  // every refresh of a guarded route to /unauthorized; wait instead.
  if (requiredScope && isRestoringScopes) {
    return null;
  }

  if (requiredScope && !hasScope(requiredScope)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
